import React, { useState } from 'react';
import { useErrorStore } from '../store/useErrorStore';
import {
  downloadDonationsReport,
  fetchDonationItemAnalytics,
  type DonationItemAnalyticsResponse,
} from '../api';
import { Box, Button, Typography, FormControl, FormLabel, Stack, Select, Option, Card, Table, Alert } from '@mui/joy';
import dayjs from 'dayjs';
import Sidebar from "../components/common/Sidebar";
import Header from "../components/common/Header";
import JoyDatePicker from "../components/common/JoyDatePicker";
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';

const Report = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { error, setError } = useErrorStore();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [selectedRange, setSelectedRange] = useState('Last 7 Days');
  const [analytics, setAnalytics] = useState<DonationItemAnalyticsResponse | null>(null);
  const [analyticsError, setAnalyticsError] = useState('');

  React.useEffect(() => {
    const today = dayjs();
    switch (selectedRange) {
      case 'Today':
        setStartDate(today.format('YYYY-MM-DD'));
        setEndDate(today.format('YYYY-MM-DD'));
        break;
      case 'Last 7 Days':
        setStartDate(today.subtract(6, 'day').format('YYYY-MM-DD'));
        setEndDate(today.format('YYYY-MM-DD'));
        break;
      case 'Last 1 Month':
        setStartDate(today.subtract(1, 'month').format('YYYY-MM-DD'));
        setEndDate(today.format('YYYY-MM-DD'));
        break;
      case 'Last 3 Months':
        setStartDate(today.subtract(3, 'month').format('YYYY-MM-DD'));
        setEndDate(today.format('YYYY-MM-DD'));
        break;
      case 'Custom':
      default:
        break;
    }
  }, [selectedRange]);

  React.useEffect(() => {
    const loadAnalytics = async () => {
      if (!startDate || !endDate) return;

      const start = dayjs(startDate);
      const end = dayjs(endDate);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return;
      if (start.isAfter(dayjs()) || end.isAfter(dayjs())) {
        setAnalytics(null);
        setAnalyticsError('Date range cannot be in the future.');
        return;
      }
      if (end.diff(start, 'day') > 366) {
        setAnalytics(null);
        setAnalyticsError('Maximum allowed range is 1 year.');
        return;
      }

      setIsLoadingAnalytics(true);
      setAnalyticsError('');
      try {
        const response = await fetchDonationItemAnalytics(startDate, endDate);
        setAnalytics(response);
      } catch (err) {
        console.error(err);
        setAnalytics(null);
        setAnalyticsError('Unable to load item analytics right now.');
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    loadAnalytics();
  }, [startDate, endDate]);

  const formatCurrency = (value: number) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

  const handleDownload = async () => {
    setError('');
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    if (end.isBefore(start)) {
      setError('End date cannot be before start date.');
      return;
    }
    if (start.isAfter(dayjs()) || end.isAfter(dayjs())) {
      setError('Dates cannot be in the future.');
      return;
    }

    if (end.diff(start, 'day') > 366) {
      setError('Maximum allowed range is 1 year.');
      return;
    }

    setIsDownloading(true);
    try {
      const blob = await downloadDonationsReport(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `donations_report_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error(err);
      setError('An error occurred while downloading the report.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: {
            xs: "calc(12px + var(--Header-height))",
            sm: "calc(12px + var(--Header-height))",
            md: 3,
          },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          gap: 1,
          bgcolor: "background.body",
        }}
      >
        <Box sx={{ flex: 1, width: "100%" }}>
          <Box
            sx={{
              position: "sticky",
              top: { sm: -100, md: -110 },
              bgcolor: "background.body",
            }}
          >


            <Box sx={{ px: { xs: 2, md: 6 } }}>
              <PageBreadcrumbs
                links={[]}
                current="Report"
              />
            </Box>

            <Stack spacing={4} sx={{ display: "flex", maxWidth: "800px", mx: "auto", px: { xs: 2, md: 6 }, py: { xs: 2, md: 3 } }}>
              <Card>
                <Box>
                  <Typography level="h3" component="h2" sx={{ mb: 2 }}>
                    Download Report
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ my: 1 }}>
                    <FormControl>
                      <FormLabel>Report Type</FormLabel>
                      <Select size="sm" sx={{ minWidth: 200 }} value="Donation" disabled>
                        <Option value="Donation">Donation</Option>
                        <Option value="Expense" disabled>Expense (Coming Soon)</Option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Select Range</FormLabel>
                      <Select size='sm' sx={{ minWidth: 200 }} value={selectedRange} onChange={(_, val) => setSelectedRange(val || 'Custom')}>
                        <Option value="Today">Today</Option>
                        <Option value="Last 7 Days">Last 7 Days</Option>
                        <Option value="Last 1 Month">Last 1 Month</Option>
                        <Option value="Last 3 Months">Last 3 Months</Option>
                        <Option value="Custom">Custom</Option>
                      </Select>
                    </FormControl>
                  </Stack>

                  {selectedRange === 'Custom' && (
                    <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                      <FormControl>
                        <FormLabel>Start Date</FormLabel>
                        <JoyDatePicker
                          name="startDate"
                          value={startDate ? dayjs(startDate) : null}
                          onChange={(newValue) => setStartDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                          maxDate={dayjs()}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                height: "32px",
                                minHeight: "32px",
                                fontSize: "0.875rem",
                                padding: "5px 10px",
                              },
                            },
                          }}
                          sx={{
                            height: "32px",
                            minHeight: "32px",
                            width: "200px",
                            "& .MuiInputBase-root": { height: "32px" }
                          }}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>End Date</FormLabel>
                        <JoyDatePicker
                          name="endDate"
                          value={endDate ? dayjs(endDate) : null}
                          onChange={(newValue) => setEndDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                          minDate={startDate ? dayjs(startDate) : undefined}
                          maxDate={dayjs()}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                height: "32px",
                                minHeight: "32px",
                                fontSize: "0.875rem",
                                padding: "5px 10px",
                              },
                            },
                          }}
                          sx={{
                            height: "32px",
                            minHeight: "32px",
                            width: "200px",
                            "& .MuiInputBase-root": { height: "32px" }
                          }}
                        />
                      </FormControl>
                    </Stack>
                  )}

                  <> </>
                  {error && <Typography color="danger">{error}</Typography>}
                  <Button loading={isDownloading} onClick={handleDownload} sx={{ mt: 1 }}>
                    {isDownloading ? 'Downloading...' : 'Download Report'}
                  </Button>
                </Box>
              </Card>

              <Card>
                <Box>
                  <Typography level="h3" component="h2" sx={{ mb: 1 }}>
                    Donation Item Analytics
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Item-wise insights for the selected date range.
                  </Typography>

                  {isLoadingAnalytics ? (
                    <Typography>Loading item analytics...</Typography>
                  ) : analyticsError ? (
                    <Alert color="warning" variant="soft">{analyticsError}</Alert>
                  ) : analytics ? (
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
                          gap: 1,
                        }}
                      >
                        <Card variant="soft"><Typography level="title-sm">Line Items</Typography><Typography level="h4">{analytics.summary.totalLineItems}</Typography></Card>
                        <Card variant="soft"><Typography level="title-sm">Total Quantity</Typography><Typography level="h4">{analytics.summary.totalQuantity}</Typography></Card>
                        <Card variant="soft"><Typography level="title-sm">Total Amount</Typography><Typography level="h4">{formatCurrency(analytics.summary.totalAmount)}</Typography></Card>
                        <Card variant="soft"><Typography level="title-sm">Unique Items</Typography><Typography level="h4">{analytics.summary.uniqueItems}</Typography></Card>
                        <Card variant="soft"><Typography level="title-sm">Unique Donors</Typography><Typography level="h4">{analytics.summary.uniqueDonors}</Typography></Card>
                      </Box>

                      <Box>
                        <Typography level="title-md" sx={{ mb: 1 }}>Top Donation Items</Typography>
                        <Table size="sm" borderAxis="bothBetween">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Page</th>
                              <th>Qty</th>
                              <th>Donations</th>
                              <th>Unique Donors</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.topItems.length === 0 ? (
                              <tr>
                                <td colSpan={6}>No item records found for this date range.</td>
                              </tr>
                            ) : (
                              analytics.topItems.map((item) => (
                                <tr key={item.itemId}>
                                  <td>{item.itemName || item.itemId}</td>
                                  <td>{item.pagePath || '-'}</td>
                                  <td>{item.totalQuantity}</td>
                                  <td>{item.donationCount}</td>
                                  <td>{item.uniqueDonors}</td>
                                  <td>{formatCurrency(item.totalAmount)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </Box>

                      <Box>
                        <Typography level="title-md" sx={{ mb: 1 }}>By Page</Typography>
                        <Table size="sm" borderAxis="bothBetween">
                          <thead>
                            <tr>
                              <th>Page</th>
                              <th>Unique Items</th>
                              <th>Total Quantity</th>
                              <th>Total Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.byPage.length === 0 ? (
                              <tr>
                                <td colSpan={4}>No page-level aggregates available.</td>
                              </tr>
                            ) : (
                              analytics.byPage.map((row) => (
                                <tr key={row.pagePath}>
                                  <td>{row.pagePath}</td>
                                  <td>{row.uniqueItems}</td>
                                  <td>{row.totalQuantity}</td>
                                  <td>{formatCurrency(row.totalAmount)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </Box>

                      <Box>
                        <Typography level="title-md" sx={{ mb: 1 }}>Recent Item Contributions</Typography>
                        <Table size="sm" borderAxis="bothBetween" sx={{ '--TableCell-headFontSize': '0.72rem', '--TableCell-bodyFontSize': '0.78rem' }}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Donor</th>
                              <th>Item</th>
                              <th>Qty</th>
                              <th>Unit</th>
                              <th>Line Total</th>
                              <th>Donation ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.recentContributions.length === 0 ? (
                              <tr>
                                <td colSpan={7}>No recent item contributions in this range.</td>
                              </tr>
                            ) : (
                              analytics.recentContributions.slice(0, 30).map((row) => (
                                <tr key={`${row.donationId}-${row.itemId}-${row.date}`}>
                                  <td>{dayjs(row.date).format('DD MMM YYYY')}</td>
                                  <td>{row.donorName || row.donorPhone || row.donorEmail || '-'}</td>
                                  <td>{row.itemName || row.itemId}</td>
                                  <td>{row.quantity}</td>
                                  <td>{formatCurrency(row.unitAmount)}</td>
                                  <td>{formatCurrency(row.lineTotal)}</td>
                                  <td>{row.donationId}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography level="body-sm" color="neutral">
                      No item analytics available for this date range.
                    </Typography>
                  )}
                </Box>
              </Card>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Report;
